import { useEffect, useMemo, useState } from "react"

import { ReactComponent as IconEdit } from "@common_assets/svg/edit.svg"
import { ReactComponent as IconFilters } from "@common_assets/svg/filters.svg"
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  IconButton,
  Skeleton,
  Stack,
  Theme,
  Tooltip,
  Typography,
} from "@mui/material"
import {
  useMutationBulkDeleteGroups,
  useMutationDeleteGroup,
  useMutationSaveGroupFilter,
} from "api/reactQuery/mutations/groups"
import { useQueryGroupFilter, useQueryGroups } from "api/reactQuery/queries/groups"
import { useQueryUserMe } from "api/reactQuery/queries/userMe"
import { ExportButton } from "components/common/exportMenu"
import GroupCard from "components/common/groupCard"
import GroupFilterMenu from "components/common/groupFilterMenu"
import NoElementsMessage from "components/common/listing/noElementsMessage"
import TransactionsDialogForm from "components/module/classStudentsTeacher/common/viewCommonElementsContainer/transactionsDialogForm/TransactionsDialogForm"
import { useArchivedClassGuard } from "hooks/archivedClassGuard"
import { useClassId } from "hooks/navigation"
import { userIsFreeDemoSchoolPlan } from "hooks/schoolPlans"
import { useCustomSnackbar } from "hooks/snackbar"
import { useTranslation } from "react-i18next"
import { IGroup } from "ts/interfaces/Group"
import { getErrorMessageFromTab } from "utils/api"

import AddGroupCard from "./components/AddGroupCard"
import AddGroupMenu from "./components/AddGroupMenu"
import CreateGroupDialog from "./components/CreateGroupDialog"
import DeleteGroupsDialog from "./components/DeleteGroupsDialog"
import EditGroupDialog from "./components/EditGroupDialog"
import ExportGroupsMenu from "./components/ExportGroupsMenu"
import GroupsEmptyState from "./components/GroupsEmptyState"
import GroupsUpgradePrompt from "./components/GroupsUpgradePrompt"
import ImportGroupsDialog from "./components/ImportGroupsDialog"

const GRID_SIZE = { mobile: 12, tablet: 6, desktop: 3 }

const roundIconButtonSx = (theme: Theme) => ({
  width: "48px",
  height: "48px",
  color: theme.palette.primary.main,
  border: `1px solid ${theme.palette.primary.main}`,
  bgcolor: theme.palette.common.white,
  transition: "background .3s",
  "&:hover": {
    bgcolor: theme.palette.mockup.primary90,
  },
})

type DeleteTarget =
  | { kind: "single"; group: IGroup }
  | { kind: "bulk"; groups: IGroup[] }

const Groups = () => {
  const { t } = useTranslation()
  const classId = Number(useClassId())
  const { showSnackbar } = useCustomSnackbar()
  const { isLoading: isUserMeLoading } = useQueryUserMe()
  const isFreeDemo = userIsFreeDemoSchoolPlan()
  const { isArchived } = useArchivedClassGuard()

  const { data, isLoading: isGroupsLoading } = useQueryGroups({
    classId,
    options: {
      enabled: !isUserMeLoading && !isFreeDemo && !isArchived && !!classId,
    },
  })
  const groups = useMemo(() => data?.data ?? [], [data])

  const { data: filterData } = useQueryGroupFilter({
    classId,
    options: {
      enabled: !isUserMeLoading && !isFreeDemo && !isArchived && !!classId,
    },
  })
  const groupsTabFilter = filterData?.data.groups_tab ?? null
  const isFilterActive =
    groupsTabFilter !== null &&
    (groupsTabFilter.length !== groups.length ||
      groups.some((group) => !groupsTabFilter.includes(group.id)))
  const visibleGroups = useMemo(() => {
    const filtered =
      groupsTabFilter === null
        ? groups
        : groups.filter((group) => groupsTabFilter.includes(group.id))
    // Class-scoped groups first, school-wide groups after — never interleaved.
    return [...filtered].sort((a, b) =>
      a.scope === b.scope ? 0 : a.scope === "SCHOOL" ? 1 : -1,
    )
  }, [groups, groupsTabFilter])

  const { mutate: saveGroupFilter } = useMutationSaveGroupFilter()

  const [isEditMode, setIsEditMode] = useState(false)
  const [selectedIds, setSelectedIds] = useState<number[]>([])
  const [addAnchor, setAddAnchor] = useState<HTMLElement | null>(null)
  const [exportAnchor, setExportAnchor] = useState<HTMLElement | null>(null)
  const [filterAnchor, setFilterAnchor] = useState<HTMLElement | null>(null)
  const [isCreateOpen, setIsCreateOpen] = useState(false)
  const [isImportOpen, setIsImportOpen] = useState(false)
  const [editGroup, setEditGroup] = useState<IGroup | null>(null)
  const [transactionGroup, setTransactionGroup] = useState<IGroup | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null)

  // School-wide groups have no card checkbox (GroupCard) — exclude them from bulk-select
  // everywhere so "select all" / bulk-delete can never reach them through the toolbar.
  const selectableGroups = useMemo(
    () => visibleGroups.filter((group) => group.scope !== "SCHOOL"),
    [visibleGroups],
  )

  const selectedGroups = useMemo(
    () => selectableGroups.filter((group) => selectedIds.includes(group.id)),
    [selectableGroups, selectedIds],
  )
  const isAllSelected =
    selectableGroups.length > 0 &&
    selectedGroups.length === selectableGroups.length
  const isSomeSelected =
    selectedGroups.length > 0 && selectedGroups.length < selectableGroups.length

  const toggleSelect = (id: number) =>
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((el) => el !== id) : [...prev, id],
    )

  const toggleEditMode = () => {
    if (isEditMode) setSelectedIds([])
    setIsEditMode((prev) => !prev)
  }

  useEffect(() => {
    if (groups.length === 0) {
      setIsEditMode(false)
      setSelectedIds([])
    }
  }, [groups.length])

  const toggleSelectAll = () =>
    setSelectedIds(
      isAllSelected ? [] : selectableGroups.map((group) => group.id),
    )

  const { mutate: deleteGroup, isLoading: isDeletingGroup } =
    useMutationDeleteGroup({
      options: {
        onSuccess: (_, variables) => {
          showSnackbar({ title: t("group.groupDeletedSuccess") })
          setSelectedIds((prev) =>
            prev.filter((id) => id !== variables.groupId),
          )
          setDeleteTarget(null)
        },
        onError: (error) =>
          showSnackbar({
            title: t(getErrorMessageFromTab(error)),
            variant: "error",
          }),
      },
    })

  const { mutate: bulkDeleteGroups, isLoading: isBulkDeleting } =
    useMutationBulkDeleteGroups({
      options: {
        onSuccess: () => {
          showSnackbar({ title: t("group.groupsDeletedSuccess") })
          setSelectedIds([])
          setDeleteTarget(null)
        },
        onError: (error) =>
          showSnackbar({
            title: t(getErrorMessageFromTab(error)),
            variant: "error",
          }),
      },
    })

  const confirmDelete = () => {
    if (!deleteTarget) return
    if (deleteTarget.kind === "single") {
      deleteGroup({ classId, groupId: deleteTarget.group.id })
    } else {
      bulkDeleteGroups({
        classId,
        group_ids: deleteTarget.groups.map((group) => group.id),
      })
    }
  }

  if (isUserMeLoading || isGroupsLoading) {
    return (
      <Grid container spacing="16px" mt="8px">
        {Array.from({ length: 8 }).map((_, index) => (
          <Grid key={index} size={GRID_SIZE}>
            <Skeleton variant="rounded" height={220} />
          </Grid>
        ))}
      </Grid>
    )
  }

  if (isFreeDemo) return <GroupsUpgradePrompt />

  const openAddMenu = (anchor: HTMLElement) => setAddAnchor(anchor)
  const isEmpty = groups.length === 0

  return (
    <Stack gap="16px">
      {!isEmpty && (
        <Stack direction="row" flexWrap="wrap" alignItems="center" gap="8px">
          {isEditMode && (
            <FormControlLabel
              sx={{ mr: 0, ml: "-4px", flexShrink: 0 }}
              control={
                <Checkbox
                  checked={isAllSelected}
                  indeterminate={isSomeSelected}
                  onChange={toggleSelectAll}
                />
              }
              label={
                <Typography
                  variant="body2"
                  fontWeight={700}
                  color="mockup.neutral10"
                >
                  {t("group.selectAll")}
                </Typography>
              }
            />
          )}
          <Stack
            direction={{ mobile: "column-reverse", tablet: "row" }}
            gap="8px"
            width={{ mobile: "100%", tablet: "auto" }}
            ml={{ tablet: "auto" }}
            order={{ mobile: 3, tablet: 2 }}
            sx={{
              "& .MuiButton-root": {
                width: { mobile: "100%", tablet: "auto" },
              },
            }}
          >
            {isEditMode && (
              <Button
                variant="outlined"
                color="error"
                disabled={selectedGroups.length === 0}
                onClick={() =>
                  setDeleteTarget({ kind: "bulk", groups: selectedGroups })
                }
                sx={(theme) => ({
                  "&.Mui-disabled": {
                    color: theme.palette.error.main,
                    borderColor: theme.palette.error.main,
                    opacity: 0.5,
                  },
                })}
              >
                {t("group.deleteSelected")}
              </Button>
            )}
            <Button onClick={(e) => openAddMenu(e.currentTarget)}>
              {t("group.addGroup")}
            </Button>
          </Stack>
          <Stack
            direction="row"
            gap="8px"
            justifyContent={{ mobile: "center", tablet: "flex-end" }}
            flexShrink={0}
            order={{ mobile: 2, tablet: 3 }}
            sx={{
              flexBasis: { mobile: "100%", tablet: "auto" },
            }}
          >
            <Tooltip
              title={
                isEditMode ? t("group.exitEditMode") : t("group.editGroups")
              }
              arrow
              placement="top"
            >
              <IconButton
                aria-label={t("group.editGroups")}
                aria-pressed={isEditMode}
                onClick={toggleEditMode}
                sx={(theme) => ({
                  ...roundIconButtonSx(theme),
                  ...(isEditMode && {
                    color: theme.palette.mockup.neutral100,
                    bgcolor: theme.palette.primary.main,
                    "&:hover": {
                      bgcolor: theme.palette.mockup.primary20,
                      color: theme.palette.mockup.neutral100,
                    },
                  }),
                })}
              >
                <IconEdit />
              </IconButton>
            </Tooltip>
            <Tooltip title={t("group.filter")} arrow placement="top">
              <IconButton
                aria-label={t("group.filter")}
                aria-haspopup="true"
                aria-expanded={Boolean(filterAnchor)}
                onClick={(e) => setFilterAnchor(e.currentTarget)}
                sx={(theme) => ({
                  ...roundIconButtonSx(theme),
                  ...(isFilterActive && {
                    color: theme.palette.mockup.neutral100,
                    bgcolor: theme.palette.primary.main,
                    "&:hover": {
                      bgcolor: theme.palette.mockup.primary20,
                      color: theme.palette.mockup.neutral100,
                    },
                  }),
                })}
              >
                <IconFilters />
              </IconButton>
            </Tooltip>
            <Box display="flex">
              <ExportButton
                isFetching={false}
                onClick={(e) => setExportAnchor(e.currentTarget)}
              />
            </Box>
          </Stack>
        </Stack>
      )}

      {isEmpty ? (
        <GroupsEmptyState
          onImportGroup={() => setIsImportOpen(true)}
          onCreateGroup={() => setIsCreateOpen(true)}
        />
      ) : visibleGroups.length === 0 ? (
        <NoElementsMessage
          boxStyle={{ py: { mobile: "16px", tablet: "62px" } }}
          title={t("group.allGroupsHiddenTitle")}
          description={t("group.allGroupsHiddenDescription")}
        />
      ) : (
        <Box bgcolor="mockup.primary99" p="16px" borderRadius="8px">
          <Grid container spacing="16px">
            {visibleGroups.map((group) => (
              <Grid key={group.id} size={GRID_SIZE}>
                <GroupCard
                  group={group}
                  isSelected={selectedIds.includes(group.id)}
                  onToggleSelect={() => toggleSelect(group.id)}
                  onCardClick={() =>
                    isEditMode
                      ? toggleSelect(group.id)
                      : setTransactionGroup(group)
                  }
                  readOnly={!isEditMode}
                  selectMode={isEditMode}
                  onEdit={() => setEditGroup(group)}
                  onDelete={() => setDeleteTarget({ kind: "single", group })}
                  disableSchoolWideActions
                  showSendTransactionCta
                />
              </Grid>
            ))}
            <Grid size={GRID_SIZE}>
              <AddGroupCard onClick={openAddMenu} />
            </Grid>
          </Grid>
        </Box>
      )}

      <AddGroupMenu
        anchorEl={addAnchor}
        open={Boolean(addAnchor)}
        onClose={() => setAddAnchor(null)}
        onCreateNew={() => setIsCreateOpen(true)}
        onUploadImport={() => setIsImportOpen(true)}
      />
      <ExportGroupsMenu
        anchorEl={exportAnchor}
        open={Boolean(exportAnchor)}
        onClose={() => setExportAnchor(null)}
        classId={classId}
        groupIds={isFilterActive ? (groupsTabFilter ?? undefined) : undefined}
        isExportDisabled={visibleGroups.length === 0}
      />
      <GroupFilterMenu
        anchorEl={filterAnchor}
        open={Boolean(filterAnchor)}
        onClose={() => setFilterAnchor(null)}
        groups={groups}
        savedSelection={groupsTabFilter}
        title={t("group.displayGroups")}
        helperText={t("group.selectGroupsToDisplay")}
        onApply={(ids) => {
          saveGroupFilter({ classId, data: { groups_tab: ids } })
          setFilterAnchor(null)
        }}
        onReset={() => {
          saveGroupFilter({ classId, data: { groups_tab: null } })
          setFilterAnchor(null)
        }}
      />

      <CreateGroupDialog
        open={isCreateOpen}
        classId={classId}
        onClose={() => setIsCreateOpen(false)}
        onCreated={(group) => {
          if (groupsTabFilter !== null) {
            saveGroupFilter({
              classId,
              data: { groups_tab: [...groupsTabFilter, group.id] },
            })
          }
        }}
      />

      {editGroup && (
        <EditGroupDialog
          open
          classId={classId}
          group={editGroup}
          onClose={() => setEditGroup(null)}
        />
      )}

      {isImportOpen && (
        <ImportGroupsDialog
          open
          target={{ scope: "CLASS", classId }}
          onClose={() => setIsImportOpen(false)}
        />
      )}

      {deleteTarget && (
        <DeleteGroupsDialog
          open
          groupNames={
            deleteTarget.kind === "single"
              ? [deleteTarget.group.name]
              : deleteTarget.groups.map((group) => group.name)
          }
          isLoading={isDeletingGroup || isBulkDeleting}
          onClose={() => setDeleteTarget(null)}
          onConfirm={confirmDelete}
        />
      )}

      {transactionGroup && (
        <TransactionsDialogForm
          isOpen
          groupId={transactionGroup.id}
          action="group"
          defaultExpandedSections={["bonus"]}
          handleClose={() => setTransactionGroup(null)}
          onSuccess={() => setTransactionGroup(null)}
        />
      )}
    </Stack>
  )
}

export default Groups
